import React, { useState, useEffect } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Button,
    IconButton,
    Chip,
    Select,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ViewList as ViewListIcon,
    GridView as GridViewIcon
} from "@mui/icons-material";
import { StackContainer } from '../../../Stack'; // Adjust path as needed

export const StackCraft = ({
    name = "StackCraft",
    rootId = null,
    debug = false,
    rows = 100,
    clickable = true,
    showCapture = true,
    sideBySide = false,
    listWidth = "350px",
    width = "350px",
    height = "400px",
    detailIds = [
        { name: "Card View", id: "card" },
        { name: "Detail", id: "683e93bc913b1fff02ade7d9" },
        { name: "Todo", id: "68430718feccebbb4b8e7551" }
    ],
    showViewSelector = true,
    // NEW GRID PROPS
    viewMode = "tree", // "tree" or "grid"
    columnsPerRow = 3,
    imageWidth = "64px",
    ratio = "1/1",
    imageClassName = "",
    textClassName = "",
    animateClass = "",
    style = {},
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Use detailIds as provided
    const safeDetailIds = detailIds || [
        { name: "Card View", id: "card" },
        { name: "Detail", id: "683e93bc913b1fff02ade7d9" },
        { name: "Todo", id: "68430718feccebbb4b8e7551" }
    ];

    const getSelectionStyle = () => {
        if (!enabled) return {};

        if (isSelected) {
            return {
                outline: '1px dashed #2196f3',
                outlineOffset: '2px'
            };
        }
        if (isHovered) {
            return {
                outline: '1px dashed #ff9800',
                outlineOffset: '1px'
            };
        }
        return {};
    };

    const containerStyle = {
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
    };

    return (
        <div
            ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
            name={name}
            style={containerStyle}
            {...props}
        >
            <StackContainer
                rootId={rootId}
                debug={debug}
                rows={rows}
                showDetailView={clickable}
                detailIds={safeDetailIds}
                showViewSelector={showViewSelector}
                showCapture={showCapture}
                sideBySide={sideBySide}
                listWidth={listWidth}
                // NEW GRID PROPS
                viewMode={viewMode}
                columnsPerRow={columnsPerRow}
                imageWidth={imageWidth}
                ratio={ratio}
                imageClassName={imageClassName}
                textClassName={textClassName}
                animateClass={animateClass}
            />
        </div>
    );
};

const StackCraftSettings = () => {
    const {
        actions: { setProp },
        name,
        rootId,
        debug,
        rows,
        clickable,
        showCapture,
        sideBySide,
        listWidth,
        width,
        height,
        detailIds,
        showViewSelector,
        // NEW GRID SETTINGS
        viewMode,
        columnsPerRow,
        imageWidth,
        ratio,
        imageClassName,
        textClassName,
        animateClass
    } = useNode((node) => ({
        name: node.data.props.name,
        rootId: node.data.props.rootId,
        debug: node.data.props.debug,
        rows: node.data.props.rows,
        clickable: node.data.props.clickable,
        showCapture: node.data.props.showCapture,
        sideBySide: node.data.props.sideBySide,
        listWidth: node.data.props.listWidth,
        width: node.data.props.width,
        height: node.data.props.height,
        detailIds: node.data.props.detailIds || [
            { name: "Card View", id: "68440556fde8885af4b96e3e" },
            { name: "Detail", id: "683e93bc913b1fff02ade7d9" },
            { name: "Todo", id: "68430718feccebbb4b8e7551" }
        ],
        showViewSelector: node.data.props.showViewSelector,
        // NEW GRID SETTINGS
        viewMode: node.data.props.viewMode,
        columnsPerRow: node.data.props.columnsPerRow,
        imageWidth: node.data.props.imageWidth,
        ratio: node.data.props.ratio,
        imageClassName: node.data.props.imageClassName,
        textClassName: node.data.props.textClassName,
        animateClass: node.data.props.animateClass
    }));

    const [localName, setLocalName] = useState(name);
    const [localRootId, setLocalRootId] = useState(rootId);
    const [localWidth, setLocalWidth] = useState(width);
    const [localHeight, setLocalHeight] = useState(height);
    const [localListWidth, setLocalListWidth] = useState(listWidth);
    const [useSelfRoot, setUseSelfRoot] = useState(rootId === "self");

    // For adding new views
    const [newViewName, setNewViewName] = useState('');
    const [newViewId, setNewViewId] = useState('');

    // For editing existing views
    const [editingIndex, setEditingIndex] = useState(null);
    const [editName, setEditName] = useState('');
    const [editId, setEditId] = useState('');

    useEffect(() => setLocalName(name), [name]);
    useEffect(() => setLocalRootId(rootId || ''), [rootId]);
    useEffect(() => setLocalWidth(width), [width]);
    useEffect(() => setLocalHeight(height), [height]);
    useEffect(() => setLocalListWidth(listWidth), [listWidth]);
    useEffect(() => setUseSelfRoot(rootId === "self"), [rootId]);

    const handleAddView = () => {
        if (newViewName.trim() && newViewId.trim()) {
            const exists = detailIds.some(view => view.id === newViewId.trim());
            if (!exists) {
                setProp(props => props.detailIds = [
                    ...detailIds,
                    { name: newViewName.trim(), id: newViewId.trim() }
                ]);
                setNewViewName('');
                setNewViewId('');
            }
        }
    };

    const handleRemoveView = (index) => {
        if (detailIds.length > 1) {
            setProp(props => props.detailIds = detailIds.filter((_, i) => i !== index));
        }
    };

    const handleStartEdit = (index) => {
        setEditingIndex(index);
        setEditName(detailIds[index].name);
        setEditId(detailIds[index].id);
    };

    const handleSaveEdit = () => {
        if (editName.trim() && editId.trim()) {
            const exists = detailIds.some((view, i) => i !== editingIndex && view.id === editId.trim());
            if (!exists) {
                setProp(props => {
                    const newDetailIds = [...detailIds];
                    newDetailIds[editingIndex] = { name: editName.trim(), id: editId.trim() };
                    props.detailIds = newDetailIds;
                });
                setEditingIndex(null);
                setEditName('');
                setEditId('');
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditName('');
        setEditId('');
    };

    return (
        <div>
            <FormControl fullWidth margin="normal">
                <FormLabel>Component Name</FormLabel>
                <TextField
                    value={localName || ''}
                    onChange={(e) => setLocalName(e.target.value)}
                    onBlur={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Root ID (leave empty to use current user)</FormLabel>
                <TextField
                    value={localRootId || ''}
                    onChange={(e) => setLocalRootId(e.target.value)}
                    onBlur={(e) => setProp(props => props.rootId = e.target.value || null)}
                    size="small"
                    placeholder="Leave empty for current user"
                    disabled={useSelfRoot}
                />
            </FormControl>

            {/* NEW VIEW MODE SECTION */}
            <FormControl fullWidth margin="normal">
                <FormLabel>View Mode</FormLabel>
                <Select
                    value={viewMode || "tree"}
                    onChange={(e) => setProp(props => props.viewMode = e.target.value)}
                    size="small"
                >
                    <MenuItem value="tree">
                        <ViewListIcon style={{ marginRight: '8px' }} />
                        Tree View
                    </MenuItem>
                    <MenuItem value="grid">
                        <GridViewIcon style={{ marginRight: '8px' }} />
                        Grid View
                    </MenuItem>
                </Select>
            </FormControl>

            {/* GRID-SPECIFIC SETTINGS */}
            {viewMode === "grid" && (
                <Box style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '12px', 
                    marginTop: '8px',
                    backgroundColor: '#f9f9f9'
                }}>
                    <FormLabel style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                        Grid Settings
                    </FormLabel>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Columns Per Row</FormLabel>
                        <TextField
                            type="number"
                            value={columnsPerRow || 3}
                            onChange={(e) => setProp(props => props.columnsPerRow = parseInt(e.target.value) || 3)}
                            size="small"
                            inputProps={{ min: 1, max: 8 }}
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image Width</FormLabel>
                        <TextField
                            value={imageWidth || "64px"}
                            onChange={(e) => setProp(props => props.imageWidth = e.target.value)}
                            size="small"
                            placeholder="64px, 80px, 100px"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image Ratio</FormLabel>
                        <Select
                            value={ratio || "1/1"}
                            onChange={(e) => setProp(props => props.ratio = e.target.value)}
                            size="small"
                        >
                            <MenuItem value="1/1">Square (1:1)</MenuItem>
                            <MenuItem value="4/3">Standard (4:3)</MenuItem>
                            <MenuItem value="16/9">Widescreen (16:9)</MenuItem>
                            <MenuItem value="3/4">Portrait (3:4)</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image CSS Classes</FormLabel>
                        <TextField
                            value={imageClassName || ""}
                            onChange={(e) => setProp(props => props.imageClassName = e.target.value)}
                            size="small"
                            placeholder="rounded-lg shadow-md border"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Text CSS Classes</FormLabel>
                        <TextField
                            value={textClassName || ""}
                            onChange={(e) => setProp(props => props.textClassName = e.target.value)}
                            size="small"
                            placeholder="text-sm font-medium"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Animation Classes (Animate.css)</FormLabel>
                        <TextField
                            value={animateClass || ""}
                            onChange={(e) => setProp(props => props.animateClass = e.target.value)}
                            size="small"
                            placeholder="fadeIn, bounceIn, slideInUp"
                        />
                    </FormControl>
                </Box>
            )}

            <FormControlLabel
                control={
                    <Switch
                        checked={showViewSelector}
                        onChange={(e) => setProp(props => props.showViewSelector = e.target.checked)}
                    />
                }
                label="Show View Selector Dropdown"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>Detail Views</FormLabel>
                <Box style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                    {/* Existing views list */}
                    {detailIds.map((view, index) => (
                        <Box key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                            padding: '8px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '4px'
                        }}>
                            {editingIndex === index ? (
                                <>
                                    <TextField
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        size="small"
                                        placeholder="View name"
                                        style={{ flex: '1' }}
                                    />
                                    <TextField
                                        value={editId}
                                        onChange={(e) => setEditId(e.target.value)}
                                        size="small"
                                        placeholder="View ID"
                                        style={{ flex: '1' }}
                                    />
                                    <IconButton onClick={handleSaveEdit} size="small" color="primary">
                                        <CheckIcon />
                                    </IconButton>
                                    <IconButton onClick={handleCancelEdit} size="small">
                                        <CloseIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    <Box style={{ flex: '1' }}>
                                        <div style={{ fontWeight: 'bold' }}>{view.name}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>{view.id}</div>
                                    </Box>
                                    <IconButton onClick={() => handleStartEdit(index)} size="small">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleRemoveView(index)}
                                        size="small"
                                        disabled={detailIds.length <= 1}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    ))}

                    {/* Add new view */}
                    <Box style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid #eee'
                    }}>
                        <TextField
                            value={newViewName}
                            onChange={(e) => setNewViewName(e.target.value)}
                            size="small"
                            placeholder="View name"
                            style={{ flex: '1' }}
                        />
                        <TextField
                            value={newViewId}
                            onChange={(e) => setNewViewId(e.target.value)}
                            size="small"
                            placeholder="View ID"
                            style={{ flex: '1' }}
                        />
                        <IconButton
                            onClick={handleAddView}
                            disabled={!newViewName.trim() || !newViewId.trim() ||
                                detailIds.some(view => view.id === newViewId.trim())}
                            size="small"
                            color="primary"
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>
                </Box>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Container Width (total width - use 350px for replace mode, 700px+ for side-by-side)</FormLabel>
                <TextField
                    value={localWidth}
                    onChange={(e) => setLocalWidth(e.target.value)}
                    onBlur={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    placeholder="350px for replace, 700px+ for side-by-side"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Height</FormLabel>
                <TextField
                    value={localHeight}
                    onChange={(e) => setLocalHeight(e.target.value)}
                    onBlur={(e) => setProp(props => props.height = e.target.value)}
                    size="small"
                    placeholder="400px, 100vh, auto"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Max Rows Per Level</FormLabel>
                <TextField
                    type="number"
                    value={rows}
                    onChange={(e) => setProp(props => props.rows = parseInt(e.target.value) || 100)}
                    size="small"
                    inputProps={{ min: 10, max: 1000 }}
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={clickable}
                        onChange={(e) => setProp(props => props.clickable = e.target.checked)}
                    />
                }
                label="Clickable Items (Enable Detail View)"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={sideBySide}
                        onChange={(e) => setProp(props => props.sideBySide = e.target.checked)}
                    />
                }
                label="Side-by-Side Mode (otherwise detail replaces list)"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>List Width (for side-by-side mode)</FormLabel>
                <TextField
                    value={localListWidth}
                    onChange={(e) => setLocalListWidth(e.target.value)}
                    onBlur={(e) => setProp(props => props.listWidth = e.target.value)}
                    size="small"
                    placeholder="350px, 50%, 450px"
                    helperText="Controls width of list panel when in side-by-side mode"
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={showCapture}
                        onChange={(e) => setProp(props => props.showCapture = e.target.checked)}
                    />
                }
                label="Show Capture Component"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={useSelfRoot}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setUseSelfRoot(newValue);
                            setProp(props => props.rootId = newValue ? "self" : null);
                            if (newValue) {
                                setLocalRootId("self");
                            }
                        }}
                    />
                }
                label="Use Self as Root (current card _id)"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={debug}
                        onChange={(e) => setProp(props => props.debug = e.target.checked)}
                    />
                }
                label="Debug Mode"
            />
        </div>
    );
};

StackCraft.craft = {
    props: {
        name: "StackCraft",
        rootId: null,
        debug: false,
        rows: 100,
        clickable: true,
        showCapture: true,
        sideBySide: false,
        listWidth: "350px",
        width: "350px",
        height: "400px",
        detailIds: [
            { name: "Card View", id: "68440556fde8885af4b96e3e" },
            { name: "Detail", id: "683e93bc913b1fff02ade7d9" },
            { name: "Todo", id: "68430718feccebbb4b8e7551" }
        ],
        showViewSelector: true,
        // NEW GRID DEFAULT PROPS
        viewMode: "tree",
        columnsPerRow: 3,
        imageWidth: "64px",
        ratio: "1/1",
        imageClassName: "",
        textClassName: "",
        animateClass: ""
    },
    related: {
        settings: StackCraftSettings
    },
    displayName: "StackCraft"
};